import { apiClient } from '../../../api/client'
import { downloadXlsx } from '../../../lib/download'
import type {
  MarkAllReadResult,
  Notification,
  NotificationQuery,
  PagedResponse,
  UnreadCount,
} from '../types'

/**
 * Bildirim merkezi uçları (058).
 *
 * **`/management` yoktur — kasıtlı**: bu uçlar anayasa v1.18.0'ın *kişisel kapsam* grubundadır
 * (kimlik doğrulaması ister, grant istemez; sunucu her sorguyu oturum siciline daraltır).
 * Precedent: `GET /api/v1/me`.
 */
const BASE = '/notifications'

/**
 * Sayfalı bildirim listesi. **Zil paneli de bunu kullanır** (`size: 5`) — ayrı bir "son 5" ucu yoktur.
 *
 * `sort` **gönderilmez**: düzen sunucuda sabittir (`plannedSendingTime` azalan). `from`/`to` yalnız
 * doluysa eklenir; boş parametre göndermek sunucuda gereksiz bir filtre dalı üretir.
 */
export async function getNotifications({
  page,
  size,
  status,
  from,
  to,
}: NotificationQuery): Promise<PagedResponse<Notification>> {
  const { data } = await apiClient.get<PagedResponse<Notification>>(BASE, {
    params: {
      page,
      size,
      status,
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    },
  })
  return data
}

/** Rozetin okunmamış sayacı (spec FR-007). Liste ucundan bağımsız çağrılır. */
export async function getUnreadCount(): Promise<UnreadCount> {
  const { data } = await apiClient.get<UnreadCount>(`${BASE}/unread-count`)
  return data
}

/**
 * Tek bildirimi okundu işaretler (spec FR-017) ve güncellenmiş satırı döner.
 *
 * **Gövde göndermez ve `modDate` taşımaz** — sunucu tarafında geçiş bir *compare-and-set*'tir ve uç
 * hiçbir alan içeriği almaz. Bu yüzden `409` beklenmez; aynı isteği iki kez göndermek güvenlidir.
 */
export async function markNotificationRead(id: number): Promise<Notification> {
  const { data } = await apiClient.put<Notification>(`${BASE}/${id}/read`)
  return data
}

/**
 * Tek bildirimi **okunmadı** işaretler (spec FR-017a) — {@link markNotificationRead}'in aynası.
 * Kullanıcı arayüzdeki okundu anahtarını kapattığında çağrılır; aynı şekilde idempotenttir.
 */
export async function markNotificationUnread(id: number): Promise<Notification> {
  const { data } = await apiClient.put<Notification>(`${BASE}/${id}/unread`)
  return data
}

/**
 * Kullanıcının **okunmamış tüm** bildirimlerini okundu işaretler (spec FR-033).
 *
 * **Filtre göndermez — kasıtlı:** eylemin kapsamı listedeki filtrelerden ve görünen sayfadan
 * bağımsızdır; uç zaten parametre kabul etmez.
 */
export async function markAllNotificationsRead(): Promise<MarkAllReadResult> {
  const { data } = await apiClient.put<MarkAllReadResult>(`${BASE}/read-all`)
  return data
}

/**
 * Bildirim listesini Excel (.xlsx) olarak indirir (ekrandaki durum/tarih filtreleriyle).
 *
 * Liste ucuyla aynı filtreler; sunucu **tüm** filtreli kayıtları döndürür (sayfalama yok). İndirme
 * mekaniği ve dosya adı ortak {@link downloadXlsx} yardımcısındadır (boş filtreler gönderilmez; ad
 * sunucunun Content-Disposition'ından okunur).
 */
export async function exportNotifications({ status, from, to }: Omit<NotificationQuery, 'page' | 'size'>): Promise<void> {
  await downloadXlsx(`${BASE}/export`, { status, from, to }, 'bildirimler.xlsx')
}
